/**
 * Zaman kuralı (CLAUDE.md 8): saklama UTC, "gün" Europe/Istanbul'a göre.
 */
export const ISTANBUL_TZ = 'Europe/Istanbul';

/**
 * BİÇİMLEYİCİ ÖNBELLEĞİ.
 *
 * `new Intl.DateTimeFormat(...)` pahalıdır (~0,15 ms) ve bu işlevler satır başına çağrılıyor.
 * 30 günlük raporda 3.500 teslimat satırı için biçimleyici kurmak tek başına ~1 saniye
 * tutuyordu: veritabanı işi 120 ms, geri kalanı biçimleyici kurulumuydu (Faz 8 performans
 * ölçümü). Biçimleyici durum tutmaz; saat dilimi başına bir kez kurulur.
 */
const dayFormatters = new Map<string, Intl.DateTimeFormat>();

function dayFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = dayFormatters.get(timeZone);
  if (!formatter) {
    // en-CA biçimi ISO benzeri YYYY-MM-DD üretir.
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    dayFormatters.set(timeZone, formatter);
  }
  return formatter;
}

/** Verilen anın Istanbul gününü "YYYY-MM-DD" olarak döner. */
export function istanbulDayString(at: Date, timeZone: string = ISTANBUL_TZ): string {
  return dayFormatter(timeZone).format(at);
}

/** Verilen anın Istanbul saatini (0-23) döner. Rapor saat grafiği satır başına çağırır. */
export function istanbulHourOf(at: Date, timeZone: string = ISTANBUL_TZ): number {
  return Number(hourFormatter(timeZone).format(at)) % 24;
}

const hourFormatters = new Map<string, Intl.DateTimeFormat>();

function hourFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = hourFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', hour12: false });
    hourFormatters.set(timeZone, formatter);
  }
  return formatter;
}

/**
 * Verilen anın Istanbul gününü, Prisma `@db.Date` kolonuna yazılmaya uygun UTC gece yarısı `Date`'i olarak döner.
 * Örn. 2026-09-03T22:30:00Z (Istanbul 04.09 01:30) → 2026-09-04T00:00:00Z.
 */
export function istanbulDayOf(at: Date, timeZone: string = ISTANBUL_TZ): Date {
  const [y, m, d] = istanbulDayString(at, timeZone).split('-').map(Number) as [
    number,
    number,
    number,
  ];
  return new Date(Date.UTC(y, m - 1, d));
}

/**
 * Bir Istanbul gününün UTC olarak [başlangıç, bitiş) aralığı.
 *
 * Kasa ve rapor sorguları "gün" bazlıdır ama zamanlar UTC saklanır (kural 8). Aralığı UTC gece
 * yarısına göre kurmak Türkiye'de 3 saatlik kayma üretir: gece 00:00-03:00 arasındaki tahsilat
 * bir önceki güne düşer. Kayma, saat farkının kendisinden hesaplanır — sabit +03 varsayılmaz ki
 * yaz saati uygulaması geri gelirse kod sessizce yanlışlamasın.
 */
export function istanbulDayRange(
  day: string,
  timeZone: string = ISTANBUL_TZ,
): { from: Date; to: Date } {
  const from = istanbulDayStart(day, timeZone);
  // Gün SONU, ertesi günün başlangıcıdır — "başlangıç + 24 saat" DEĞİL. Yaz saati uygulaması
  // geri gelirse geçiş günü 23 ya da 25 saat sürer; sabit 24 saat eklemek o gün bir saati çift
  // sayar ya da kaybeder ve kasa özeti ile gün serisi ayrışır (Faz 3 denetimi N1).
  const to = istanbulDayStart(shiftIsoDay(day, 1), timeZone);
  return { from, to };
}

/** Bir Istanbul gününün UTC başlangıcı. */
function istanbulDayStart(day: string, timeZone: string): Date {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const utcMidnight = Date.UTC(y, m - 1, d);
  // Ofset, günün kendi ofsetiyle hesaplanır; iki adımda yakınsar (DST sınırında bile).
  let at = new Date(utcMidnight - timeZoneOffsetMs(new Date(utcMidnight), timeZone));
  at = new Date(utcMidnight - timeZoneOffsetMs(at, timeZone));
  return at;
}

/**
 * Istanbul duvar saatindeki bir anı (ör. panelde "29.09.26 15:04") UTC `Date`'e çevirir.
 *
 * Sürecin saat dilimine BAĞLI DEĞİL: `new Date(y, m, d, hh, mm)` süreç TZ'siyle yorumlar ve
 * sunucu `TZ: UTC` çalıştığı için panel saatleri üç saat kayık yazılıyordu. Ay 1 tabanlıdır.
 * Taşan değerler (ay 13, saat 25) `Date.UTC` gibi ileri taşınır — yerel kurucuyla aynı davranış.
 */
export function istanbulWallTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  ms = 0,
  timeZone: string = ISTANBUL_TZ,
): Date {
  // Milisaniye ofset hesabının DIŞINDA tutulur: `timeZoneOffsetMs` saniyeye yuvarlanmış parçalardan
  // çalışır, kesirli anda ofset milisaniye kadar sapardı.
  const wall = Date.UTC(year, month - 1, day, hour, minute, second);
  // `istanbulDayStart` ile aynı iki adımlı yakınsama: ofset, hedef anın kendi ofsetidir.
  let at = new Date(wall - timeZoneOffsetMs(new Date(wall), timeZone));
  at = new Date(wall - timeZoneOffsetMs(at, timeZone));
  return new Date(at.getTime() + ms);
}

/**
 * Verilen anın Istanbul duvar saatini soneksiz ISO biçiminde döner: "2026-09-29T15:04:00".
 * Saat dilimi soneki bilerek YOK — panellerin "yerel saat" beklediği gövde alanları için.
 */
export function istanbulLocalIso(at: Date, timeZone: string = ISTANBUL_TZ): string {
  const shifted = new Date(at.getTime() + timeZoneOffsetMs(at, timeZone));
  return shifted.toISOString().slice(0, 19);
}

/** "YYYY-MM-DD" gününe n gün ekler. */
export function shiftIsoDay(day: string, delta: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
}

/** Verilen anda saat diliminin UTC'ye göre farkı (ms; UTC+3 için +3s). */
function timeZoneOffsetMs(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  // `hour` 24 dönebilir (gece yarısı); UTC.  24 = ertesi günün 0'ı olarak doğru toplanır.
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return asUtc - at.getTime();
}
